/**
 * Sword spectacle (lane C0-VFX-PREP, the user 2026-10-10: "remove the ink-brush pattern; make spectacle like the golden
 * dragon palm"): sword-qi forms drawn in our own pixel style, in the move kind's light (attack vermilion, break amber,
 * power gold, …) with the master's clean dark outline and light from the upper right. Original designs; wuxia ideas
 * only (a qi blade surging forward, swords flown by qi 御劍, a rain of swords, a giant sword 巨劍), nothing traced.
 *
 * - `drawBeam`       a qi blade beam surging from the hand through the foe, a sword at its head (thrusts)
 * - `drawFlying`     swords summoned behind the caster, aimed, flown at the foe one after another (ranged sword moves)
 * - `drawFlyingOne`  one flown sword (a volley's shot)
 * - `drawRain`       swords falling from the sky round the target and stabbing into the ground (sword areas)
 * - `drawGiant`      a giant sword formed above the foe, dropped point-first into the ground, then shattered (power)
 *
 * Coordinates as the ink samples (render/ink/samples.ts): art px local to the caster's feet, +x toward the foe, the foe
 * at (`D`, `tdy`). Every sword is a cached picture per (size, angle step, tone): one drawImage a frame each.
 */
import { clamp01, easeIn, easeOut, hash2, span } from './noise';
import { INK_PALETTES, type InkTone } from './palette';
import { foeX, P, type InkLayer, type InkScene } from './samples';
import { detailQuality, drawPic, gone, pic, ringPic, type Pic } from './detail/draw';
import type { Ramp } from './detail/ramps';

/** Angle steps of a cached sword (a full turn). */
const STEPS = 32;
const STEEL = { body: '#e4f0ff', fuller: '#9fb2d4', lit: '#ffffff', shade: '#7486aa' } as const;
const GOLD = { mid: '#f2c64a', lit: '#fff0a8', dark: '#8a6414' } as const;
const GRIP = { mid: '#7a1c24', dark: '#3e0a10' } as const;
const TASSEL = ['#e0283a', '#a01424'] as const;

/** The kind's light as a five-tone ramp (outline … white), for the rings drawn with the detail layer's pictures. */
export function rampOf(kind: InkTone): Ramp {
  const p = INK_PALETTES[kind];
  return [p.accDark, p.accDark, p.acc, p.accLit, '#ffffff'];
}

/**
 * A sword `L` art px long pointing at angle step `step` (0 = +x, screen angles, +y down), blade half-width `bw`, a qi
 * halo `halo` cells wide in the tone's accent and a dark outline round it; a red tassel off the pommel (`tassel`), and
 * `fade` (0–3) dithering it away. Centre of the picture = the sword's middle.
 */
function swordPic(kind: InkTone, L: number, bw: number, halo: number, step: number, tassel: boolean, fade: number): Pic {
  const p = INK_PALETTES[kind];
  const R = Math.ceil(L / 2 + halo + 3);
  return pic(`sword|${kind}|${L}|${bw}|${halo}|${step}|${tassel ? 1 : 0}|${fade}`, -R, -R, R + 1, R + 6, (set) => {
    const a = (step / STEPS) * Math.PI * 2;
    const cx = Math.cos(a);
    const cy = Math.sin(a);
    const gU = Math.max(5, Math.round(L * 0.2));
    const tipLen = Math.max(3, L * 0.16);
    const half = (u: number): number => {
      if (u < 2) return 1.2; // pommel
      if (u < gU - 1) return 0.9; // grip
      if (u < gU + 1) return bw * 2.3 + 0.6; // guard
      if (u > L - tipLen) return Math.max(0.35, bw * ((L - u) / tipLen));
      return bw;
    };
    for (let y = -R; y <= R; y++)
      for (let x = -R; x <= R; x++) {
        if (fade > 0 && ((x * 3 + y * 5) & 3) < fade) continue;
        const u = x * cx + y * cy + L / 2;
        const v = -x * cy + y * cx;
        if (u < -halo - 1 || u > L + halo + 1) continue;
        const uc = Math.max(0, Math.min(L, u));
        const hw = half(uc);
        const av = Math.abs(v);
        const blade = uc >= gU + 1;
        const inside = u >= 0 && u <= L && av <= hw;
        if (!inside) {
          // Qi halo round the blade only (not the grip), then the dark outline round everything.
          const out = Math.max(av - hw, u < 0 ? -u : u > L ? u - L : 0);
          if (blade && out <= halo) set(x, y, out <= halo * 0.5 ? p.accLit : p.acc, 1);
          else if (out <= (blade ? halo : 0) + 1.1) set(x, y, p.accDark, 1);
          continue;
        }
        // The side of the blade facing the light (upper right) is lit.
        const nx = v > 0 ? -cy : cy;
        const ny = v > 0 ? cx : -cx;
        const litSide = nx * 0.7 - ny * 0.7 > 0;
        let col: string;
        if (u < 2) col = av < 0.6 ? GOLD.lit : GOLD.mid;
        else if (u < gU - 1) col = Math.round(u) % 2 ? GRIP.mid : GRIP.dark;
        else if (u < gU + 1) col = av > hw - 0.9 ? GOLD.dark : litSide ? GOLD.lit : GOLD.mid;
        else if (av > hw - 0.9) col = litSide ? STEEL.lit : STEEL.shade;
        else if (av < 0.6 && u < L - tipLen) col = bw >= 4 && Math.round(u) % 7 === 0 ? GOLD.mid : STEEL.fuller;
        else col = u > L - tipLen * 0.6 ? p.accLit : STEEL.body;
        set(x, y, col, 1);
      }
    if (tassel) {
      // The red tassel hangs from the pommel (screen down), two cords.
      const px = Math.round(-cx * (L / 2 + 1));
      const py = Math.round(-cy * (L / 2 + 1));
      for (let k = 1; k <= 5; k++) {
        set(px + (k > 3 ? 1 : 0), py + k, k % 2 ? TASSEL[0] : TASSEL[1], 1);
        if (k > 1) set(px - 1 + (k > 4 ? 1 : 0), py + k, TASSEL[1], 1);
      }
    }
  });
}

/** The angle step of a direction (local, the caster facing +x). */
const stepOf = (dx: number, dy: number): number => ((Math.round((Math.atan2(dy, dx) / (Math.PI * 2)) * STEPS) % STEPS) + STEPS) % STEPS;

/** A sword whose tip is at local (`tx`, `ty`), pointing along (`dx`, `dy`). */
function swordAt(g: CanvasRenderingContext2D, sc: InkScene, kind: InkTone, L: number, bw: number, halo: number, tx: number, ty: number, dx: number, dy: number, tassel = true, fade = 0): void {
  const n = Math.hypot(dx, dy) || 1;
  const step = stepOf(dx, dy);
  drawPic(g, sc, swordPic(kind, L, bw, halo, step, tassel, fade), tx - (dx / n) * (L / 2), ty - (dy / n) * (L / 2));
}

/** A four-point flash, white core and accent arms, `r` art px. */
function flash(g: CanvasRenderingContext2D, sc: InkScene, kind: InkTone, lx: number, ly: number, r: number): void {
  const p = INK_PALETTES[kind];
  const [X, Y] = P(sc, lx, ly);
  const x = Math.round(X);
  const y = Math.round(Y);
  g.fillStyle = p.acc;
  g.fillRect(x - r, y - 1, r * 2 + 1, 3);
  g.fillRect(x - 1, y - r, 3, r * 2 + 1);
  g.fillStyle = '#ffffff';
  g.fillRect(x - r + 2, y, r * 2 - 3, 1);
  g.fillRect(x, y - r + 2, 1, r * 2 - 3);
  g.fillRect(x - 2, y - 2, 5, 5);
}

/** A short trail of qi cells behind a moving point (head light, tail accent). */
function trail(g: CanvasRenderingContext2D, sc: InkScene, kind: InkTone, x0: number, y0: number, x1: number, y1: number): void {
  const p = INK_PALETTES[kind];
  const n = Math.max(2, Math.round(Math.hypot(x1 - x0, y1 - y0) / 3));
  for (let i = 0; i < n; i++) {
    const u = i / n;
    const [X, Y] = P(sc, x0 + (x1 - x0) * u, y0 + (y1 - y0) * u);
    g.fillStyle = u > 0.6 ? p.accLit : u > 0.25 ? p.acc : p.accDark;
    g.fillRect(Math.round(X), Math.round(Y), u > 0.5 ? 2 : 1, u > 0.5 ? 2 : 1);
  }
}

/** Shards flying out of a broken sword (accent and steel cells), `s` s after it breaks. */
function shards(g: CanvasRenderingContext2D, sc: InkScene, kind: InkTone, cx: number, cy: number, s: number, n: number, reach: number, seed: number): void {
  if (s < 0 || s > 0.45) return;
  const p = INK_PALETTES[kind];
  for (let i = 0; i < n; i++) {
    if (gone(i, span(s, 0.2, 0.45), seed)) continue;
    const a = (i / n) * Math.PI * 2 + hash2(i, 1, seed);
    const r = reach * easeOut(s / 0.45) * (0.5 + 0.6 * hash2(i, 2, seed));
    const [X, Y] = P(sc, cx + Math.cos(a) * r, cy + Math.sin(a) * r * 0.7 + 60 * s * s);
    g.fillStyle = i % 3 ? p.accLit : STEEL.lit;
    g.fillRect(Math.round(X), Math.round(Y), 2, i % 2 ? 1 : 2);
  }
}

// ── the beam (a thrust) ──

const BEAM: Map<string, HTMLCanvasElement> = new Map();
/** A qi blade beam picture: `len` art px along +x, rising by `slope` per px, thickness step `thin` 0 (full)–3. */
function beamCanvas(kind: InkTone, len: number, slope: number, thin: number): { c: HTMLCanvasElement; y0: number } {
  const lb = Math.round(len / 8) * 8;
  const sb = Math.round(slope * 20) / 20;
  const k = `${kind}|${lb}|${sb}|${thin}`;
  const pad = 18;
  const y0 = Math.min(0, sb * lb) - pad;
  let c = BEAM.get(k);
  if (!c) {
    const p = INK_PALETTES[kind];
    const H = Math.ceil(Math.abs(sb * lb) + pad * 2);
    c = document.createElement('canvas');
    c.width = lb * 2;
    c.height = H * 2;
    const g = c.getContext('2d')!;
    for (let x = 0; x < lb; x++) {
      const u = x / lb;
      // Thin at the hand, full toward the head; thinner as it fades.
      const th = (2.5 + 7 * Math.min(1, u * 1.6)) * (1 - thin * 0.25);
      const yc = sb * x - y0;
      for (let r = -Math.ceil(th) - 1; r <= Math.ceil(th) + 1; r++) {
        const ar = Math.abs(r);
        if (thin > 0 && ((x + r * 3) & 3) < thin) continue;
        const col = ar > th ? p.accDark : ar < 1 ? '#ffffff' : ar < th * 0.5 ? p.accLit : p.acc;
        g.fillStyle = col;
        g.fillRect(x * 2, Math.round((yc + r) * 2), 2, 2);
      }
      // Speed dashes above and below the beam.
      if (u > 0.2 && (x % 11 < 5) && hash2(x >> 3, 1, 1300) > 0.4) {
        g.fillStyle = p.accLit;
        g.fillRect(x * 2, Math.round((yc - th - 4) * 2), 2, 2);
        g.fillRect(x * 2, Math.round((yc + th + 4) * 2), 2, 2);
      }
    }
    BEAM.set(k, c);
  }
  return { c, y0 };
}

/** The beam `t` s into its template (blow at `hit`): it surges out, passes through the foe, then thins away. */
export function drawBeam(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, kind: InkTone, hit: number): void {
  if (layer !== 'over' || t < 0) return;
  const x0 = 12;
  const y0 = -44;
  const x1 = foeX(sc) + 60;
  const y1 = sc.tdy - 44 + (sc.tdy * 60) / Math.max(1, sc.D);
  const len = x1 - x0;
  const slope = (y1 - y0) / Math.max(1, len);
  // The head reaches the foe on the blow and the far end a little after.
  const head = clamp01(easeIn(clamp01(t / (hit + 0.05))));
  const thin = Math.min(3, Math.floor(span(t, hit + 0.12, hit + 0.42) * 4));
  if (t > hit + 0.42) return;
  const { c, y0: py } = beamCanvas(kind, len, slope, thin);
  const shown = Math.max(2, Math.round(c.width * head));
  const [X, Y] = P(sc, x0, y0 + py);
  g.save();
  g.translate(Math.round(X), Math.round(Y));
  if (sc.flip) g.scale(-1, 1);
  g.drawImage(c, 0, 0, shown, c.height, 0, 0, shown / 2, c.height / 2);
  g.restore();
  // The sword at the head while it flies, a glint at the far end when it is spent.
  const hx = x0 + len * head;
  const hy = y0 + slope * len * head;
  if (t < hit + 0.14) swordAt(g, sc, kind, 46, 3, 2, hx + 10, hy, 1, slope, false);
  if (t >= hit - 0.02 && t < hit + 0.08) flash(g, sc, kind, foeX(sc), sc.tdy - 44, 10);
}

// ── flown swords (御劍) ──

/** Where the `i`th of `n` summoned swords hovers, behind and above the caster. */
const hoverAt = (i: number, n: number): [number, number] => {
  const k = n === 1 ? 0 : i / (n - 1) - 0.5;
  return [-10 + k * 52, -92 - (0.5 - Math.abs(k)) * 22];
};

/**
 * `n` swords summoned behind the caster (0–0.1 s), aimed at the foe, then flown one after another, the first meeting
 * the foe on the blow (`hit`), the rest 0.05 s apart; each bursts into shards as it strikes.
 */
export function drawFlying(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, kind: InkTone, hit: number, n = 5): void {
  if (t < 0) return;
  // The swords' landing ring on the ground, under the foe (QA VFX-R1 m1: a ground hit).
  if (layer === 'ground') {
    const s = t - hit;
    if (s >= 0 && s < 0.45) {
      const r = Math.round(8 + 30 * easeOut(s / 0.3));
      drawPic(g, sc, ringPic(rampOf(kind), r, Math.round(r * 0.42), Math.min(3, Math.floor(span(s, 0.2, 0.45) * 4)), 2), foeX(sc), sc.tdy);
    }
    return;
  }
  const fx = foeX(sc);
  const fy = sc.tdy - 42;
  for (let i = 0; i < n; i++) {
    const [hx, hy] = hoverAt(i, n);
    const bob = Math.sin(t * 14 + i) * 1.5;
    const arrive = hit + i * 0.05;
    const launch = arrive - 0.14;
    const tx = fx + (i - (n - 1) / 2) * 4;
    const ty = fy + (i - (n - 1) / 2) * 6;
    if (t < launch) {
      // Summoned: a flash where it appears, then it hovers aiming at the foe (the tell of a ranged sword move).
      const appear = span(t, i * 0.03, i * 0.03 + 0.1);
      if (appear <= 0) continue;
      swordAt(g, sc, kind, 40, 2.4, 1, hx, hy + bob, tx - hx, ty - hy, true, appear < 1 ? 3 - Math.floor(appear * 3) : 0);
      if (appear < 1) flash(g, sc, kind, hx, hy, 5);
      continue;
    }
    if (t < arrive) {
      const u = easeIn((t - launch) / (arrive - launch));
      const x = hx + (tx - hx) * u;
      const y = hy + (ty - hy) * u;
      trail(g, sc, kind, hx + (tx - hx) * Math.max(0, u - 0.45), hy + (ty - hy) * Math.max(0, u - 0.45), x, y);
      swordAt(g, sc, kind, 40, 2.4, 1, x, y, tx - hx, ty - hy, true);
      continue;
    }
    const s = t - arrive;
    if (s < 0.06) flash(g, sc, kind, tx, ty, 7);
    shards(g, sc, kind, tx, ty, s, 8, 26, 1310 + i);
  }
}

/** One flown sword of a volley, `tau` s from its landing (it flies in over the last 0.2 s). */
export function drawFlyingOne(g: CanvasRenderingContext2D, tau: number, sc: InkScene, kind: InkTone): void {
  if (tau < -0.3 || tau > 0.45) return;
  const [hx, hy] = [-6, -78];
  const tx = foeX(sc);
  const ty = sc.tdy - 42;
  if (tau < -0.2) {
    swordAt(g, sc, kind, 34, 2.2, 1, hx, hy, tx - hx, ty - hy, true, Math.min(3, Math.floor(((-0.2 - tau) / 0.1) * 3)));
    return;
  }
  if (tau < 0) {
    const u = easeIn((tau + 0.2) / 0.2);
    const x = hx + (tx - hx) * u;
    const y = hy + (ty - hy) * u;
    trail(g, sc, kind, hx + (tx - hx) * Math.max(0, u - 0.4), hy + (ty - hy) * Math.max(0, u - 0.4), x, y);
    swordAt(g, sc, kind, 34, 2.2, 1, x, y, tx - hx, ty - hy, true);
    return;
  }
  if (tau < 0.06) flash(g, sc, kind, tx, ty, 6);
  shards(g, sc, kind, tx, ty, tau, 6, 22, 1320);
}

// ── sword rain ──

/** The rain's swords round the target: offset, landing time before the blow, size. */
function rainSwords(n: number): { ox: number; oy: number; at: number; big: boolean; tilt: number }[] {
  const out = [{ ox: 0, oy: 0, at: 0, big: true, tilt: 0 }];
  for (let i = 0; i < n - 1; i++) {
    const a = (i / (n - 1)) * Math.PI * 2 + 0.4;
    const r = 30 + 14 * hash2(i, 1, 1330);
    out.push({ ox: Math.cos(a) * r, oy: Math.sin(a) * r * 0.42, at: -0.2 + 0.18 * (i / (n - 1)), big: false, tilt: (hash2(i, 2, 1330) - 0.5) * 0.5 });
  }
  return out;
}

/**
 * Swords rain round the target: each appears high above, falls, and stabs into the ground with a ring and dust; the
 * centre one, the biggest, lands on the blow (`hit`). They stand in the ground a while, then dissolve.
 * Swords behind the target are drawn on the ground layer (under the figures), the rest over them.
 */
export function drawRain(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, kind: InkTone, hit: number): void {
  const n = Math.max(5, Math.round(11 * detailQuality()));
  const cx = foeX(sc);
  const cy = sc.tdy;
  const R = rampOf(kind);
  for (const [i, s] of rainSwords(n).entries()) {
    const land = hit + s.at;
    const tau = t - land;
    if (tau < -0.16 || tau > 0.75) continue;
    const L = s.big ? 46 : 28;
    const bw = s.big ? 2.6 : 1.6;
    const gx = cx + s.ox;
    const gy = cy + s.oy + 3;
    const behind = s.oy < 0;
    const dx = Math.sin(s.tilt);
    const dy = Math.cos(s.tilt);
    if (layer === 'ground' && tau >= 0 && tau < 0.4) {
      // The ring and dust where it stabs in.
      const r = Math.round(4 + (s.big ? 22 : 10) * easeOut(tau / 0.25));
      drawPic(g, sc, ringPic(R, r, Math.max(2, Math.round(r * 0.42)), Math.min(3, Math.floor(span(tau, 0.15, 0.4) * 4)), s.big ? 2 : 1), gx, cy + s.oy);
    }
    if ((layer === 'ground') !== behind) continue;
    if (tau < 0) {
      // Falling: from 170 px above, a light streak behind it.
      const u = easeIn((tau + 0.16) / 0.16);
      const y = gy - 170 * (1 - u);
      const x = gx - dx * 170 * (1 - u);
      trail(g, sc, kind, x - dx * 40, y - 40, x, y);
      swordAt(g, sc, kind, L, bw, 1, x, y, dx, dy, !s.big);
      continue;
    }
    const fade = Math.min(3, Math.floor(span(tau, 0.5, 0.75) * 4));
    if (fade >= 3) continue;
    swordAt(g, sc, kind, L, bw, s.big ? 2 : 1, gx, gy, dx, dy, !s.big, fade);
    if (tau < 0.05) flash(g, sc, kind, gx, gy - 2, s.big ? 9 : 5);
    if (i === 0 && tau > 0.5) shards(g, sc, kind, gx, gy - L / 2, tau - 0.5, 10, 24, 1340);
  }
}

// ── the giant sword ──

/**
 * A giant sword (150 art px) gathered from motes above the foe, hung point-down with a glint (the tell), dropped on the
 * blow (`hit`) into the ground at the foe's feet with two rings, held, then shattered into shards and motes.
 */
export function drawGiant(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, kind: InkTone, hit: number): void {
  if (t < 0 || t > hit + 1) return;
  const L = 150;
  const fx = foeX(sc);
  const ground = sc.tdy + 5;
  const hangTip = ground - 96;
  const R = rampOf(kind);
  const form = hit - 0.2;
  if (layer === 'ground') {
    if (t >= hit && t < hit + 0.55) {
      const s = t - hit;
      const r = Math.round(10 + 50 * easeOut(s / 0.3));
      drawPic(g, sc, ringPic(R, r, Math.round(r * 0.42), Math.min(3, Math.floor(span(s, 0.25, 0.55) * 4)), 2), fx, sc.tdy);
      const r2 = Math.round(6 + 30 * easeOut(span(s, 0.06, 0.36)));
      drawPic(g, sc, ringPic(R, r2, Math.round(r2 * 0.42), Math.min(3, Math.floor(span(s, 0.3, 0.55) * 4)), 1), fx, sc.tdy);
    }
    return;
  }
  const p = INK_PALETTES[kind];
  if (t < form) {
    // Gathering: motes drawn into the blade's line, the sword dithering in.
    const u = t / form;
    for (let i = 0; i < Math.round(22 * detailQuality()); i++) {
      const a = (i / 22) * Math.PI * 2 + u * 3;
      const r = 60 * (1 - easeIn(u)) * (0.6 + 0.5 * hash2(i, 1, 1350));
      const [X, Y] = P(sc, fx + Math.cos(a) * r, hangTip - L / 2 + Math.sin(a) * r);
      g.fillStyle = i % 2 ? p.accLit : p.acc;
      g.fillRect(Math.round(X), Math.round(Y), 2, 2);
    }
    if (u > 0.35) swordAt(g, sc, kind, L, 7, 2, fx, hangTip, 0, 1, false, 3 - Math.min(3, Math.floor(((u - 0.35) / 0.65) * 4)));
    return;
  }
  if (t < hit) {
    const k = (t - form) / (hit - form);
    // Hang (the glint at the tip), then the drop.
    const drop = k < 0.45 ? 0 : easeIn((k - 0.45) / 0.55);
    const tip = hangTip + (ground - hangTip) * drop;
    if (drop > 0) for (let j = 0; j < 5; j++) trail(g, sc, kind, fx - 10 + j * 5, tip - L - 30, fx - 10 + j * 5, tip - L + 10);
    swordAt(g, sc, kind, L, 7, 2, fx, tip, 0, 1, false);
    if (drop === 0 && k > 0.15) flash(g, sc, kind, fx, hangTip + 2, 4 + Math.round(k * 8));
    return;
  }
  const s = t - hit;
  if (s < 0.08) flash(g, sc, kind, fx, ground - 4, 16);
  if (s < 0.55) {
    swordAt(g, sc, kind, L, 7, 2, fx, ground, 0, 1, false, s > 0.45 ? 1 : 0);
    return;
  }
  shards(g, sc, kind, fx, ground - L / 2, s - 0.55, Math.round(28 * detailQuality()), 70, 1360);
}

// ── the orbit (a lock form) ──

/**
 * `n` swords summoned round the caster from `t0` (a ring that turns, its back half under the figures), each launched
 * into the foe to meet it at `arrivals[i]` (target offset `dys[i]` up from the foe's chest, for a foe in the air); the
 * swords left over dissolve upward at `end` (they go into the finisher).
 */
export function drawOrbit(
  g: CanvasRenderingContext2D,
  t: number,
  sc: InkScene,
  layer: InkLayer,
  kind: InkTone,
  t0: number,
  arrivals: readonly number[],
  dys: readonly number[],
  end: number,
  n = 6,
): void {
  if (t < t0) return;
  const fx = foeX(sc);
  for (let i = 0; i < n; i++) {
    const appear = span(t, t0 + i * 0.04, t0 + i * 0.04 + 0.12);
    if (appear <= 0) continue;
    const a = (i / n) * Math.PI * 2 + (t - t0) * 3.2;
    const r = 26 * easeOut(appear);
    const ox = Math.cos(a) * r;
    const oy = -48 + Math.sin(a) * r * 0.42 + Math.sin(t * 9 + i) * 1.5;
    const behind = Math.sin(a) < 0;
    const arrive = arrivals[i];
    if (arrive !== undefined && t >= arrive - 0.14) {
      if (layer !== 'over') continue;
      const tx = fx;
      const ty = sc.tdy - 42 - (dys[i] ?? 0);
      if (t < arrive) {
        const u = easeIn((t - (arrive - 0.14)) / 0.14);
        const x = ox + (tx - ox) * u;
        const y = oy + (ty - oy) * u;
        trail(g, sc, kind, ox + (tx - ox) * Math.max(0, u - 0.45), oy + (ty - oy) * Math.max(0, u - 0.45), x, y);
        swordAt(g, sc, kind, 24, 1.5, 1, x, y, tx - ox, ty - oy, true);
      } else {
        if (t - arrive < 0.06) flash(g, sc, kind, tx, ty, 7);
        shards(g, sc, kind, tx, ty, t - arrive, 8, 24, 1370 + i);
      }
      continue;
    }
    if (t > end + 0.2 || (layer === 'ground') !== behind) continue;
    const fade = t > end ? Math.min(3, Math.floor(((t - end) / 0.2) * 4)) : appear < 1 ? 3 - Math.floor(appear * 3) : 0;
    if (fade >= 3) continue;
    swordAt(g, sc, kind, 22, 1.4, 1, ox, oy - (t > end ? (t - end) * 120 : 0), Math.cos(a) * 0.4, -1, true, fade);
  }
}
